import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Loader2, Upload, Search, ExternalLink, X, Image as ImageIcon, GripVertical, Save } from 'lucide-react';
import { PAPER_BASE } from '../config';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

export default function PaperAdminPartnerPage() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  // 새 파트너 등록
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [processWhite, setProcessWhite] = useState(false);
  const [adding, setAdding] = useState(false);
  const fileRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  // 순서 변경
  const [orderChanged, setOrderChanged] = useState(false);
  const [saving, setSaving] = useState(false);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPartners = () => {
    setLoading(true);
    axios.get(`${API}/api/paperWork/partners`)
      .then(res => {
        setPartners(res.data.partners || []);
        setOrderChanged(false);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPartners(); }, []);

  const handleSearchLogo = () => {
    const q = (name || '').trim();
    if (!q) return alert('파트너 이름을 먼저 입력해주세요.');
    window.open(`https://www.google.com/search?q=${encodeURIComponent(q + ' 로고 png')}&tbm=isch`, '_blank');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const resetForm = () => {
    setName('');
    setWebsite('');
    setProcessWhite(false);
    setSelectedFile(null);
    setPreviewUrl('');
  };

  const handleAdd = async () => {
    if (!name.trim()) return alert('파트너 이름을 입력해주세요.');
    if (!selectedFile) return alert('로고 이미지를 업로드해주세요.');

    setAdding(true);
    try {
      const formData = new FormData();
      formData.append('name', name.trim());
      formData.append('website', website.trim());
      formData.append('sortOrder', '0');
      formData.append('processWhite', processWhite ? '1' : '0');
      formData.append('logo', selectedFile);

      await axios.post(`${API}/api/paperWork/partners`, formData, {
        headers,
      });
      resetForm();
      fetchPartners();
    } catch (err) {
      alert(err.response?.data?.errorMessage || '등록 중 오류가 발생했습니다.');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id, pName) => {
    if (!window.confirm(`"${pName}" 파트너를 삭제하시겠습니까?`)) return;
    try {
      await axios.delete(`${API}/api/paperWork/partners/${id}`, { headers });
      fetchPartners();
    } catch {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  // 드래그 앤 드롭
  const handleDragStart = (index) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    if (dragItem.current === dragOverItem.current) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }
    const newPartners = [...partners];
    const draggedItem = newPartners[dragItem.current];
    newPartners.splice(dragItem.current, 1);
    newPartners.splice(dragOverItem.current, 0, draggedItem);
    setPartners(newPartners);
    setOrderChanged(true);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleSaveOrder = async () => {
    setSaving(true);
    try {
      const orders = partners.map((p, i) => ({ id: p.id, sortOrder: partners.length - i }));
      await axios.put(`${API}/api/paperWork/partners/order`, { orders }, { headers });
      setOrderChanged(false);
    } catch {
      alert('순서 저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      {/* Admin Nav */}
      <div className="flex items-center gap-4 mb-6 border-b border-white/10 pb-3 overflow-x-auto">
        <Link to={`${PAPER_BASE}/admin/members`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">회원 관리</Link>
        <Link to={`${PAPER_BASE}/admin/works`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">행사 관리</Link>
        <Link to={`${PAPER_BASE}/admin/services`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">서비스 관리</Link>
        <Link to={`${PAPER_BASE}/admin/partners`} className="text-sm font-medium text-violet-400 border-b-2 border-violet-400 pb-1 whitespace-nowrap">파트너 관리</Link>
        <Link to={`${PAPER_BASE}/admin/blogs`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">스토리 관리</Link>
      </div>

      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-light text-white tracking-wide">파트너 관리</h1>
        {orderChanged && (
          <button
            onClick={handleSaveOrder}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            순서 저장
          </button>
        )}
      </div>
      <p className="text-white/30 text-sm mb-8">메인페이지에 표시되는 파트너 로고를 관리합니다. 드래그하여 순서를 변경하세요.</p>

      {/* 새 파트너 등록 */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-8">
        <h2 className="text-white text-sm font-medium mb-4">새 파트너 등록</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-[10px] tracking-[0.15em] text-white/40 mb-1 uppercase">파트너 이름 *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 안양시"
              className="w-full bg-[#1a1428] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-violet-400/50 transition-colors"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.15em] text-white/40 mb-1 uppercase">웹사이트 (선택)</label>
            <input
              type="text"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://..."
              className="w-full bg-[#1a1428] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-violet-400/50 transition-colors"
            />
          </div>
        </div>

        {/* 로고 선택 영역 */}
        <label className="block text-[10px] tracking-[0.15em] text-white/40 mb-2 uppercase">로고 이미지 *</label>
        <div className="flex flex-wrap gap-3 mb-4">
          <button
            onClick={handleSearchLogo}
            className="flex items-center gap-1.5 px-3 py-2 bg-violet-500/20 hover:bg-violet-500/30 text-violet-400 text-sm rounded-lg transition-colors"
          >
            <Search size={13} />
            Google에서 로고 찾기
          </button>
          <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={handleFileChange} />
          <button
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 text-white/60 text-sm rounded-lg transition-colors"
          >
            <Upload size={13} />
            로고 업로드
          </button>
        </div>

        {/* 흰색 변환 옵션 */}
        <label className="flex items-center gap-2 mb-4 cursor-pointer w-fit">
          <input
            type="checkbox"
            checked={processWhite}
            onChange={(e) => setProcessWhite(e.target.checked)}
            className="w-4 h-4 rounded accent-violet-400"
          />
          <span className="text-white/50 text-xs">배경 제거 + 흰색 로고로 변환 <span className="text-white/30">(다크 배경에서 사용 시)</span></span>
        </label>

        {/* 미리보기 */}
        {previewUrl && (
          <div className="mb-4 flex items-center gap-3">
            <div className="w-16 h-16 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center p-2">
              <img src={previewUrl} alt="Preview" className="max-w-full max-h-full object-contain" />
            </div>
            <span className="text-white/40 text-xs">{selectedFile?.name}</span>
            <button onClick={() => { setPreviewUrl(''); setSelectedFile(null); }} className="text-white/40 hover:text-white">
              <X size={16} />
            </button>
          </div>
        )}

        <button
          onClick={handleAdd}
          disabled={adding}
          className="flex items-center gap-2 px-5 py-2.5 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
        >
          {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={16} />}
          파트너 등록
        </button>
      </div>

      {/* 등록된 파트너 목록 */}
      <h2 className="text-white text-sm font-medium mb-4">등록된 파트너 ({partners.length})</h2>

      {loading ? (
        <div className="text-center py-10">
          <div className="w-8 h-8 border-2 border-violet-400/30 border-t-violet-400 rounded-full animate-spin mx-auto" />
        </div>
      ) : partners.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 gap-3 text-white/30">
          <ImageIcon size={32} />
          <p className="text-sm">등록된 파트너가 없습니다.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {partners.map((p, index) => (
            <div
              key={p.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragEnter={() => handleDragEnter(index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => e.preventDefault()}
              className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden group cursor-grab active:cursor-grabbing"
            >
              <div className="aspect-square bg-white/5 flex items-center justify-center p-4 relative">
                {p.logoUrl ? (
                  <img src={p.logoUrl} alt={p.name} className="max-w-full max-h-full object-contain" />
                ) : (
                  <ImageIcon size={24} className="text-white/20" />
                )}
                {/* 드래그 핸들 */}
                <div className="absolute top-2 left-2 p-1 text-white/20 opacity-0 group-hover:opacity-100 transition-all">
                  <GripVertical size={14} />
                </div>
                <button
                  onClick={() => handleDelete(p.id, p.name)}
                  className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-red-500/80 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                  title="삭제"
                >
                  <Trash2 size={12} className="text-white" />
                </button>
              </div>
              <div className="p-3 text-center">
                <p className="text-white/80 text-xs font-medium truncate">{p.name}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
